import { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import {
  actionsOnContentOwnership,
  USER_CHANGE_CONTENT_TYPE,
} from 'shared/const';

import { deleteMember } from './graphql/mutations';

export default function useUsers({
  useGraphQLMutation,
  groupsInfo,
  invitedMembers,
  modifyContentStatus,
  organizationId,
  playlists,
  registeredMembers,
  requestToDeleteMemberGroups,
  requestToDeleteMemberPlaylists,
  requestToTransferUserGroups,
  requestToTransferUserPlaylists,
  setFormStatus,
  setModifyContentStatus,
}) {
  const { t } = useTranslation();

  const [deleteUser, setDeleteUser] = useState(false);
  const [manageContentOwnership, setManageContentOwnership] = useState(false);
  const [modifyContentTarget, setModifyContentTarget] = useState({});
  const [transferContentOwnershipFor, setTransferContentOwnershipFor] =
    useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const transferContentChoiceOptions = [
    {
      value: actionsOnContentOwnership.delete,
      label: t('administrateUserRole.deleteContent'),
    },
    {
      value: actionsOnContentOwnership.transfer,
      label: t('administrateUserRole.transferOwnershipOfContent'),
      checked: true,
    },
  ];

  const [deleteMemberMutation, deleteMemberResponse] = useGraphQLMutation(
    deleteMember,
    {
      update(cache, { data: { deleteMember } }) {
        cache.modify({
          fields: {
            getOrganization(existingOrganization = {}) {
              const membersCount = existingOrganization.membersCount - 1;
              return { ...existingOrganization, membersCount };
            },
            listOrganizations(existingOrganizations = []) {
              const newOrganizations = existingOrganizations.map(
                (organization) => {
                  if (organization.id === organizationId) {
                    const membersCount = organization.membersCount - 1;
                    return {
                      ...organization,
                      membersCount,
                    };
                  }
                  return organization;
                }
              );
              return newOrganizations;
            },
          },
        });
        cache.evict({ id: cache.identify(deleteMember) });
        cache.gc();
      },
    }
  );

  useEffect(() => {
    const { data, error, loading } = deleteMemberResponse;
    if (!loading) {
      if (error) {
        setFormStatus({
          success: false,
          message: t('administrateUsers.deletedAccessFailure'),
        });
      }
      if (data) {
        setFormStatus({
          success: true,
          message: t('administrateUsers.deletedAccessSuccess'),
        });
      }
    }
  }, [deleteMemberResponse.data, deleteMemberResponse.loading]);

  const isUserInOrganization = (email) => {
    return [...invitedMembers, ...registeredMembers].some(
      (user) => user.email === email
    );
  };

  const onDeleteUser = (user) => {
    const ownedGroups = groupsInfo.filter(
      (group) => group.groupOwner.id === user.id
    ).length;
    const ownedPlaylists = playlists.filter(
      (playlist) => playlist.ownerId === user.id
    ).length;
    const ownedContent = ownedGroups || ownedPlaylists;

    if (ownedContent) {
      setTransferContentOwnershipFor(user);
      setManageContentOwnership(true);
    } else {
      setDeleteUser(true);
    }

    setUserToDelete(user);
  };

  const onDeleteUserConfirm = (propsUserId) => {
    const { id: userId } = userToDelete;

    deleteMemberMutation({
      variables: {
        organizationId,
        userId: propsUserId || userId,
      },
    });
  };

  const onSubmitManageContentOwnership = (values) => {
    const { actionOnContent, groups, memberId, playlists } = values;

    const { user } = registeredMembers.find((member) => member.id === memberId);

    setUserToDelete(user);

    let type = '';

    if (actionOnContent === actionsOnContentOwnership.delete) {
      type = USER_CHANGE_CONTENT_TYPE.DELETE;
    } else if (actionOnContent === actionsOnContentOwnership.transfer) {
      type = USER_CHANGE_CONTENT_TYPE.TRANSFER;
    }

    const target = {
      groups: false,
      playlists: false,
      type,
    };
    const changed = { ...target };

    const actions = [];

    if (type === USER_CHANGE_CONTENT_TYPE.DELETE) {
      if (groups) {
        target.groups = true;
        actions.push(() => requestToDeleteMemberGroups(memberId, type));
      }
      if (playlists) {
        target.playlists = true;
        actions.push(() => requestToDeleteMemberPlaylists(memberId, type));
      }
    } else if (type === USER_CHANGE_CONTENT_TYPE.TRANSFER) {
      if (groups) {
        target.groups = true;
        actions.push(() =>
          requestToTransferUserGroups({ ...values, userId: user.id }, type)
        );
      }
      if (playlists) {
        target.playlists = true;
        actions.push(() =>
          requestToTransferUserPlaylists({ ...values, userId: user.id }, type)
        );
      }
    }

    setModifyContentStatus(changed);
    setModifyContentTarget(target);

    if (actions.length === 0) {
      onDeleteUserConfirm(user.id);
    } else {
      actions.forEach((action) => action());
    }
  };

  useEffect(() => {
    if (modifyContentStatus?.error) {
      setFormStatus({
        success: false,
        message: t('administrateUsers.deletedAccessFailure'),
      });
    }
    if (
      (modifyContentStatus?.type === USER_CHANGE_CONTENT_TYPE.DELETE &&
        modifyContentTarget.groups === modifyContentStatus.groups &&
        modifyContentTarget.playlists === modifyContentStatus.playlists) ||
      (modifyContentStatus?.type === USER_CHANGE_CONTENT_TYPE.TRANSFER &&
        modifyContentTarget.groups === modifyContentStatus.groups &&
        modifyContentTarget.playlists === modifyContentStatus.playlists)
    ) {
      onDeleteUserConfirm();
    }
  }, [modifyContentStatus]);

  return {
    deleteUser,
    isUserInOrganization,
    manageContentOwnership,
    onDeleteUser,
    onDeleteUserConfirm,
    onSubmitManageContentOwnership,
    setDeleteUser,
    setManageContentOwnership,
    setUserToDelete,
    transferContentChoiceOptions,
    transferContentOwnershipFor,
  };
}
